/** Manual sensors (owner, 2026-10-22): readings a person reads off a staff
 *  gauge, a dipmeter or a crack gauge and enters by hand or imports from a
 *  spreadsheet, the way Trimble 4D Control, Bentley VDV and DamData take
 *  field readings. Kept on the server since workspace v5 reached master
 *  (db/034, services/manual_sensors.py); the fork held them in this
 *  browser. What stays here is everything the browser does before a
 *  reading is sent: reading a pasted field book or a file, planning an
 *  import against what is stored (new, replacing, unchanged), the CSV
 *  export, and the series a chart draws. The server re-checks every row.
 *
 *  Every timestamp is station-local (Asia/Tbilisi) wall clock, read and
 *  written through `time.ts`. A reading's time is its instant with the
 *  station's offset written out, the one spelling of that instant, which
 *  the server sends back unchanged: one reading per instant, and entering
 *  the same time again replaces it, once the reader has said so. */
import { api } from '../api/client'
import {
  SENSOR_TYPES, type ManualReadingRecord, type ManualSensorRecord, type ManualSensorWrite, type SensorType,
  type Series, type SeriesPoint,
} from '../api/types'
import { csvTime, csvValue, type CsvFormat } from './compare'
import { remoteCollection } from './remoteStore'
import { STATION_TZ, fromLocalInput, offsetFor } from './time'

export const MANUAL_SENSORS_EVENT = 'hydro:manual-sensors-changed'
export const MAX_MANUAL_SENSORS = 100
export const MAX_MANUAL_READINGS = 10_000
export const MAX_READING_NOTE = 500
export const MAX_SENSOR_NOTES = 2000
/** A pasted file larger than this is not a field book; it is refused before
 *  parsing rather than freezing the tab on a split of megabytes of text. */
export const MAX_IMPORT_CHARACTERS = 2_000_000
/** The gateway's clock and this browser's are not synchronised, and neither
 *  is a field tablet's: a reading a few minutes ahead is a clock, not a typo.
 *  The server allows the same (manual_sensors.FUTURE_TOLERANCE). */
const FUTURE_TOLERANCE_MS = 5 * 60_000

/** A stored reading (`ManualReadingRecord`): its id, its time, value and
 *  note, who entered it and when. */
export type ManualReading = ManualReadingRecord
/** A manual sensor as the server describes it, without its readings. */
export type ManualSensor = ManualSensorRecord
/** Anything that holds readings: a sensor on its page, a plan's subject. */
export interface WithReadings { readings: ManualReading[] }
/** A sensor with its readings loaded. */
export type ManualSensorReadings = ManualSensor & WithReadings

/** What the create and edit forms collect. */
export interface ManualSensorInput {
  name: string
  sensor_type: SensorType | ''
  quantity: string
  unit: string
  precision: number
  station: string | null
  project_id: number | null
  notes: string
}

export function manualSourceId(id: string): string { return 'manual|' + id }
export function isManualSourceId(id: string): boolean { return id.startsWith('manual|') }

/** A free-text parameter cannot be matched to a stored quantity, so a manual
 *  sensor gets an axis of its own, shared only with other manual sensors of
 *  the same parameter and unit. The prefix keeps it off every raw and
 *  calculated axis: a staff gauge's metres beside a logger's metres of a
 *  different datum would otherwise flatten one of them (HANDBOOK rule 3). */
export function manualAxisGroup(parameter: string, unit: string): string {
  const quantity = parameter.trim().toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_+|_+$/g, '') || 'reading'
  return 'manual:' + quantity + ':' + unit.trim()
}

// ------------------------------------------------------------- time

const pad = (value: number, size = 2) => String(value).padStart(size, '0')

function offsetMinutes(at: Date): number {
  const match = /^([+-])(\d{2}):(\d{2})$/.exec(offsetFor(STATION_TZ, at))
  if (!match) return 0
  return (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]))
}

/** An instant as the station's wall clock with its offset. Built from the
 *  offset rather than through Intl's hour formatting, which some engines
 *  write as "24:00" at midnight, the hour every date-only reading lands on. */
export function toStationIso(at: Date): string {
  const local = new Date(at.getTime() + offsetMinutes(at) * 60_000)
  return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}`
    + `T${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}:${pad(local.getUTCSeconds())}`
    + offsetFor(STATION_TZ, at)
}

/** A station-local wall clock as a stored timestamp, or null when the date
 *  does not exist (31.02, hour 24). Checked by building the same calendar
 *  date in UTC: Date silently rolls 31 February over into March. The
 *  result must name the wall clock it was given, so an hour skipped by one
 *  of Tbilisi's old summer-time changes is refused rather than shifted.
 *  From 1950: before 1924 the zone's offset had seconds in it, which no
 *  stored timestamp can carry, and no field book here is that old. */
export function stationTimestamp(year: number, month: number, day: number,
    hour = 0, minute = 0, second = 0): string | null {
  if (!calendarTime(year, month, day, hour, minute, second)) return null
  const wall = `${pad(year, 4)}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}`
  const at = fromLocalInput(wall)
  if (!Number.isFinite(at.getTime())) return null
  const stored = toStationIso(at)
  return stored.startsWith(wall) ? stored : null
}

function calendarTime(year: number, month: number, day: number, hour: number, minute: number, second: number): boolean {
  if (year < 1950 || year > 2100 || hour > 23 || minute > 59 || second > 59) return false
  const check = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
  return check.getUTCFullYear() === year && check.getUTCMonth() === month - 1 && check.getUTCDate() === day
}

/** The date and time inputs of the entry forms (`<input type="date">` and
 *  `type="time"`), which carry no zone and are read as station time. */
export function timestampFromInputs(date: string, time: string): string | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim())
  const clock = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time.trim() || '00:00')
  if (!day || !clock) return null
  return stationTimestamp(Number(day[1]), Number(day[2]), Number(day[3]),
    Number(clock[1]), Number(clock[2]), Number(clock[3] ?? 0))
}

/** The other way: a stored timestamp split into the two inputs' values. */
export function inputsFromTimestamp(ts: string): { date: string; time: string } {
  const local = toStationIso(new Date(ts))
  const seconds = local.slice(17, 19)
  return { date: local.slice(0, 10), time: local.slice(11, seconds === '00' ? 16 : 19) }
}

/** 2026-10-22 14:30, with seconds only when a reading has them, so two
 *  readings in the same minute do not look like one in the table, nor in
 *  the chart's tooltip, which hands over the instant as epoch ms
 *  (TimeSeriesChart `pointTime`). */
export function readingTimeLabel(ts: string | number): string {
  const local = toStationIso(new Date(ts))
  const seconds = local.slice(17, 19)
  return local.slice(0, 10) + ' ' + local.slice(11, seconds === '00' ? 16 : 19)
}

const ISO_TIME = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2})(?:\.0{1,9})?)?\s*(Z|[+-]\d{2}:?\d{2})?)?$/i
const DAY_FIRST = /^(\d{1,2})([./])(\d{1,2})\2(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
const TIME_ONLY = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/

/** A date or date and time as people write them in a field book or Excel:
 *  2026-10-22 14:30, 22.10.2026 14:30 or 22/10/2026 14:30, seconds
 *  optional, a date alone meaning 00:00. Day first: Georgia writes dates day
 *  first, and a month-first file is refused (month 22) rather than read
 *  wrong. An ISO time with its own offset (this platform's export, another
 *  system's) is that instant; anything else is station wall clock. */
export function parseStationTime(text: string): string | null {
  const value = text.trim()
  const iso = ISO_TIME.exec(value)
  if (iso) {
    if (iso[7]) {
      // Checked as a calendar first, so 2026-02-31T10:00+04:00 is refused
      // rather than rolled into March by Date.parse.
      if (!calendarTime(Number(iso[1]), Number(iso[2]), Number(iso[3]), Number(iso[4]), Number(iso[5]), Number(iso[6] ?? 0))) return null
      const offset = iso[7].toUpperCase() === 'Z' ? 'Z' : iso[7].replace(/^([+-]\d{2}):?(\d{2})$/, '$1:$2')
      const at = Date.parse(`${pad(Number(iso[1]), 4)}-${pad(Number(iso[2]))}-${pad(Number(iso[3]))}`
        + `T${pad(Number(iso[4]))}:${iso[5]}:${pad(Number(iso[6] ?? 0))}${offset}`)
      return Number.isFinite(at) ? toStationIso(new Date(at)) : null
    }
    return stationTimestamp(Number(iso[1]), Number(iso[2]), Number(iso[3]),
      Number(iso[4] ?? 0), Number(iso[5] ?? 0), Number(iso[6] ?? 0))
  }
  const dayFirst = DAY_FIRST.exec(value)
  if (dayFirst) {
    return stationTimestamp(Number(dayFirst[4]), Number(dayFirst[3]), Number(dayFirst[1]),
      Number(dayFirst[5] ?? 0), Number(dayFirst[6] ?? 0), Number(dayFirst[7] ?? 0))
  }
  return null
}

/** A typed value. A decimal comma is read as the decimal point where it
 *  cannot be a separator: Excel on a Georgian or Russian machine writes
 *  1,25. Thousands separators and spaces are refused rather than guessed:
 *  1,250 is 1.25 in Tbilisi and 1250 in London, and a reading that lands
 *  a thousand times off is worse than one that asks again. */
export function parseReadingValue(raw: string, decimalComma = true): number | string {
  const text = raw.trim().replace(/\u2212/g, '-')
  if (!text) return 'Add a value.'
  if (/\s/.test(text) || (text.includes(',') && text.includes('.')) || (text.match(/,/g) ?? []).length > 1) {
    return `Write "${raw.trim()}" without spaces or thousands separators.`
  }
  if (text.includes(',') && !decimalComma) {
    return `"${raw.trim()}" has a comma in a comma-separated file. Use a decimal point.`
  }
  const normal = text.replace(',', '.')
  if (!/^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(normal)) return `"${raw.trim()}" is not a number.`
  const value = Number(normal)
  return Number.isFinite(value) ? value : `"${raw.trim()}" is too large.`
}

/** One line of a note: the table and the CSV hold a note per row. A note a
 *  spreadsheet would read as a formula was written with a leading
 *  apostrophe by the export (exportManualReadings); reading it back drops
 *  that apostrophe again, so a round trip keeps the note as typed. */
export function cleanNote(raw: string | undefined): string {
  const text = (raw ?? '').replace(/[\r\n\t]+/g, ' ').trim()
  return /^'[=+\-@]/.test(text) ? text.slice(1) : text
}

// ------------------------------------------------------------ parsing

export type ReadingSeparator = '\t' | ';' | ','
export interface ParsedReading { line: number; ts: string; value: number; note?: string }
export interface ReadingLineError { line: number; text: string; message: string }
export interface ParsedReadings {
  rows: ParsedReading[]
  errors: ReadingLineError[]
  separator: ReadingSeparator | null
  header: boolean
  /** Lines starting with #, skipped: the template's example row is one. */
  comments: number
}

/** A line the reader marked as not a reading, as the template marks its
 *  example: # first, before or inside the first cell's quotes. */
const COMMENT = /^\s*"?#/
/** Text in a first cell that a date or a time was meant to be. A first line
 *  with none is a header; one with some is a reading with a typo, and is
 *  reported rather than skipped as a title. */
const DATE_LIKE = /\d{1,4}[-./]\d{1,2}[-./]\d{1,4}|\d{1,2}:\d{2}/

/** The separator that reads the most lines as readings: each line split on
 *  each candidate, honouring quotes, and counted when its cells give a time
 *  and a value. Counting the characters instead let a note's semicolons
 *  outvote a comma file's commas. When no candidate reads a line (a header
 *  alone), the one splitting the most lines into two to four cells. A tie
 *  goes to tab, then semicolon, then comma: a semicolon file with decimal
 *  commas splits on both, and only the semicolon reads it. */
function detectSeparator(lines: string[]): ReadingSeparator | null {
  const sample = lines.slice(0, 500)
  let best: ReadingSeparator | null = null, bestReadings = 0, bestSplit = 0
  for (const candidate of ['\t', ';', ','] as const) {
    let readings = 0, split = 0
    for (const line of sample) {
      if (!line.includes(candidate)) continue
      const cells = lineCells(line, candidate)
      if (!cells || cells.length < 2 || cells.length > 4) continue
      split++
      const read = readCells(cells, candidate)
      if (read.ts && typeof read.value === 'number' && read.rest.length <= 2) readings++
    }
    if (readings > bestReadings || (readings === bestReadings && split > bestSplit)) {
      best = candidate; bestReadings = readings; bestSplit = split
    }
  }
  return best
}

/** A line's cells without the empty ones a spreadsheet leaves at its end. */
function lineCells(line: string, separator: ReadingSeparator | null): string[] | null {
  const split = splitLine(line, separator)
  if (!split) return null
  const cells = [...split]
  while (cells.length && !cells[cells.length - 1]) cells.pop()
  return cells
}

/** A line's cells read as a reading: the time (a date cell and a time cell
 *  joined, as a field book typed into Excel often has them), the value, and
 *  the cells after the time. */
function readCells(cells: string[], separator: ReadingSeparator | null) {
  let stamp = cells[0] ?? '', rest = cells.slice(1)
  if (rest.length && TIME_ONLY.test(rest[0]!) && parseStationTime(stamp) && !/\d:\d/.test(stamp)) {
    stamp = stamp + ' ' + rest[0]
    rest = rest.slice(1)
  }
  const ts = parseStationTime(stamp)
  const value: number | string = rest.length ? parseReadingValue(rest[0]!, separator !== ',') : 'Add a value.'
  return { stamp, rest, ts, value }
}

/** Split one line on the separator, honouring double quotes ("" inside a
 *  quoted cell is a quote), as Excel writes a cell that holds the separator. */
function splitLine(line: string, separator: ReadingSeparator | null): string[] | null {
  if (!separator) return [line]
  const cells: string[] = []
  let cell = '', quoted = false, i = 0
  while (i < line.length) {
    const char = line[i]!
    if (quoted) {
      if (char === '"' && line[i + 1] === '"') { cell += '"'; i += 2; continue }
      if (char === '"') { quoted = false; i++; continue }
      cell += char; i++; continue
    }
    if (char === '"' && !cell.trim()) { quoted = true; cell = ''; i++; continue }
    if (char === separator) { cells.push(cell); cell = ''; i++; continue }
    cell += char; i++
  }
  if (quoted) return null
  cells.push(cell)
  return cells.map(item => item.trim())
}

const FORMATS_HINT = 'Use 2026-10-22 14:30, 22.10.2026 14:30 or 22/10/2026 14:30.'

/** Readings pasted from Excel or a CSV file: date and time, value, and an
 *  optional note, separated by tabs, semicolons or commas, with or without a
 *  header row. Date and time may also sit in two columns, as a field book
 *  typed into Excel often has them. A line starting with # is skipped and
 *  counted. Nothing is guessed: a line that cannot be read is reported by
 *  its number and left out, and two lines for the same time with different
 *  values are both left out, since choosing either would be choosing a
 *  reading. */
export function parseReadingsText(input: string): ParsedReadings {
  const text = input.replace(/^\uFEFF/, '')
  if (text.length > MAX_IMPORT_CHARACTERS) {
    return { rows: [], errors: [{ line: 0, text: '', message: 'This text is too long to import at once. Split it into files of up to 10,000 readings.' }], separator: null, header: false, comments: 0 }
  }
  const lines = text.split(/\r\n|\n|\r/)
  const numbered = lines.map((line, index) => ({ line: index + 1, text: line })).filter(item => item.text.trim())
  const filled = numbered.filter(item => !COMMENT.test(item.text))
  const comments = numbered.length - filled.length
  let separator: ReadingSeparator | null = null
  // Excel's own "sep=;" first line names the separator outright.
  const directive = filled[0] && /^sep=(.)$/i.exec(filled[0].text.trim())
  if (directive && ['\t', ';', ','].includes(directive[1]!)) { separator = directive[1] as ReadingSeparator; filled.shift() }
  else separator = detectSeparator(filled.map(item => item.text))
  if (filled.length > MAX_MANUAL_READINGS + 1) {
    return { rows: [], errors: [{ line: 0, text: '', message: `Import up to ${MAX_MANUAL_READINGS.toLocaleString('en-GB')} readings at a time.` }], separator, header: false, comments }
  }

  const rows: ParsedReading[] = [], errors: ReadingLineError[] = []
  let header = false, first = true
  for (const { line, text: raw } of filled) {
    const fail = (message: string) => { errors.push({ line, text: raw.trim(), message }) }
    const cells = lineCells(raw, separator)
    const isFirst = first
    first = false
    if (!cells) { fail('A quotation mark is not closed.'); continue }
    if (!cells.length) continue
    const { stamp, rest, ts, value } = readCells(cells, separator)
    // A header is the first line, and only when neither its time nor its
    // value reads as one and its first cell holds nothing date-like: a
    // misspelt first reading (2026-10-32) is an error, not a title.
    if (isFirst && !ts && typeof value === 'string' && !DATE_LIKE.test(stamp)) { header = true; continue }
    if (!ts) { fail(`"${stamp}" is not a date and time. ${FORMATS_HINT}`); continue }
    if (!rest.length) { fail('Add a value after the date and time.'); continue }
    if (rest.length > 2) {
      fail('Expected date and time, value and an optional note.'
        + (separator ? ` Put a note that contains "${separator === '\t' ? 'a tab' : separator}" in double quotes.` : ''))
      continue
    }
    if (typeof value === 'string') { fail(value); continue }
    const note = cleanNote(rest[1])
    if (note.length > MAX_READING_NOTE) { fail(`Keep the note to ${MAX_READING_NOTE} characters.`); continue }
    rows.push({ line, ts, value, ...(note ? { note } : {}) })
  }

  // The same time twice: an exact repeat (a file pasted twice) is one
  // reading; two different values for one time are refused together.
  const byTime = new Map<string, ParsedReading[]>()
  for (const row of rows) byTime.set(row.ts, [...(byTime.get(row.ts) ?? []), row])
  const kept: ParsedReading[] = []
  for (const row of rows) {
    const same = byTime.get(row.ts)!
    if (same.length === 1) { kept.push(row); continue }
    const agree = same.every(other => other.value === row.value && (other.note ?? '') === (row.note ?? ''))
    if (agree) { if (same[0] === row) kept.push(row); continue }
    const others = same.filter(other => other !== row).map(other => other.line)
    errors.push({ line: row.line, text: filled.find(item => item.line === row.line)?.text.trim() ?? '',
      message: `Same time as line ${others.join(', ')} with a different reading. Neither is used; keep one.` })
  }
  errors.sort((a, b) => a.line - b.line)
  return { rows: kept, errors, separator, header, comments }
}

export type ImportEncoding = 'UTF-8' | 'UTF-16' | 'Windows-1251'

/** A chosen file's text, and how it was read. Excel writes "CSV UTF-8" as
 *  UTF-8 and "Unicode Text" as UTF-16 with a byte-order mark, both read as
 *  they are. Its plain "CSV" is the machine's ANSI code page, Windows-1251
 *  on a Russian-language Windows, so bytes that are not UTF-8 are read as
 *  that and the dialog says so: a Georgian note cannot survive an ANSI save
 *  at all, and the reader is asked to save as CSV UTF-8 instead. */
export function decodeImportBytes(bytes: Uint8Array): { text: string; encoding: ImportEncoding } {
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return { text: new TextDecoder('utf-16le').decode(bytes), encoding: 'UTF-16' }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return { text: new TextDecoder('utf-16be').decode(bytes), encoding: 'UTF-16' }
  try {
    return { text: new TextDecoder('utf-8', { fatal: true }).decode(bytes), encoding: 'UTF-8' }
  } catch {
    return { text: new TextDecoder('windows-1251').decode(bytes), encoding: 'Windows-1251' }
  }
}

// --------------------------------------------------------- readings

export interface ImportPlanRow extends ParsedReading { status: 'add' | 'replace' | 'unchanged'; previous?: ManualReading }
export interface ImportPlan {
  rows: ImportPlanRow[]
  errors: ReadingLineError[]
  add: number
  replace: number
  unchanged: number
  /** Readings the sensor would hold after applying. */
  total: number
  /** Why nothing can be applied, when something forbids it. */
  blocked?: string
}

function sortReadings(readings: ManualReading[]): ManualReading[] {
  return [...readings].sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts))
}

/** What applying these readings would do, for the preview the reader checks
 *  before Apply: each row new, replacing a stored reading (with the stored
 *  one beside it), or the same as stored. A time in the future is refused. */
export function planImport(sensor: WithReadings, parsed: Pick<ParsedReadings, 'rows' | 'errors'>,
    now: number = Date.now()): ImportPlan {
  const stored = new Map(sensor.readings.map(reading => [reading.ts, reading]))
  const errors = [...parsed.errors], rows: ImportPlanRow[] = []
  for (const row of parsed.rows) {
    if (Date.parse(row.ts) > now + FUTURE_TOLERANCE_MS) {
      errors.push({ line: row.line, text: readingTimeLabel(row.ts), message: 'This time is in the future.' })
      continue
    }
    const previous = stored.get(row.ts)
    const status = !previous ? 'add' : previous.value === row.value && (previous.note ?? '') === (row.note ?? '') ? 'unchanged' : 'replace'
    rows.push({ ...row, status, ...(previous && status === 'replace' ? { previous } : {}) })
  }
  errors.sort((a, b) => a.line - b.line)
  const add = rows.filter(row => row.status === 'add').length
  const replace = rows.filter(row => row.status === 'replace').length
  const total = sensor.readings.length + add
  return { rows, errors, add, replace, unchanged: rows.length - add - replace, total,
    ...(total > MAX_MANUAL_READINGS ? { blocked: `This would make ${total.toLocaleString('en-GB')} readings. A manual sensor holds up to ${MAX_MANUAL_READINGS.toLocaleString('en-GB')}.` } : {}) }
}

export interface ReadingEntry { ts: string; value: number; note?: string }

/** One reading typed into the form, as a one-line import. */
export function planReading(sensor: WithReadings, entry: ReadingEntry, now: number = Date.now()): ImportPlan {
  const note = cleanNote(entry.note)
  const errors: ReadingLineError[] = []
  if (!canonicalTime(entry.ts)) errors.push({ line: 1, text: '', message: 'Enter a valid date and time.' })
  if (!Number.isFinite(entry.value)) errors.push({ line: 1, text: '', message: 'Enter a number.' })
  if (note.length > MAX_READING_NOTE) errors.push({ line: 1, text: '', message: `Keep the note to ${MAX_READING_NOTE} characters.` })
  if (errors.length) return { rows: [], errors, add: 0, replace: 0, unchanged: 0, total: sensor.readings.length }
  return planImport(sensor, { rows: [{ line: 1, ts: entry.ts, value: entry.value, ...(note ? { note } : {}) }], errors: [] }, now)
}

/** Whether an edited row changes anything: saving a row nobody changed
 *  sends nothing, so its entry time keeps saying when it was last typed. */
export function editChanges(reading: ManualReading, entry: ReadingEntry): boolean {
  return entry.ts !== reading.ts || entry.value !== reading.value || (reading.note ?? '') !== cleanNote(entry.note)
}

export function findReading(sensor: WithReadings, ts: string): ManualReading | undefined {
  return sensor.readings.find(reading => reading.ts === ts)
}

/** Where the readings table hands keyboard focus once a row's edit form or
 *  delete question closes: the row's Edit button. */
export type ReadingFocus = { kind: 'row'; ts: string } | { kind: 'table' } | { kind: 'chart' }

/** The button that had the focus (Save, Keep, Delete) is gone once the row
 *  is drawn again, and the focus falls to <body> with it: a keyboard or
 *  screen-reader user starts again from the top of the page. So it goes to
 *  the reading's own row while the table still lists it, newest first and
 *  `shown` rows of it; to the table's heading when it does not (deleted,
 *  or moved by an edit past the rows shown); and to the chart's heading
 *  when no reading is left, since the table itself is gone then. */
export function readingFocusAfter(sensor: WithReadings, ts: string | null, shown: number): ReadingFocus {
  if (!sensor.readings.length) return { kind: 'chart' }
  const index = ts === null ? -1 : sensor.readings.findIndex(reading => reading.ts === ts)
  return ts !== null && index >= 0 && sensor.readings.length - 1 - index < shown ? { kind: 'row', ts } : { kind: 'table' }
}

/** The sensor's readings inside a window as a series, for the Data Explorer,
 *  Analysis and the Alarm Lab. The window is the API's own: from inclusive,
 *  to exclusive; and a cutoff, when the raw request was truncated, cuts
 *  these readings at the same instant (inclusive, `resolution.clip`) so
 *  every series on a chart covers one period. Stored readings only: nothing
 *  between them is filled in. */
export function manualSensorSeries(sensor: ManualSensorReadings, from?: Date, to?: Date, cutoff?: string | null): Series {
  const start = from?.getTime() ?? -Infinity, end = to?.getTime() ?? Infinity
  const last = cutoff ? Date.parse(cutoff) : Infinity
  const points: SeriesPoint[] = sensor.readings.flatMap(reading => {
    const at = Date.parse(reading.ts)
    return at >= start && at < end && at <= last ? [[reading.ts, reading.value, 0] as SeriesPoint] : []
  })
  return { station: 'manual', parameter: sensor.id, display_name: sensor.name, unit: sensor.unit,
    axis_group: sensor.axis_group, kind: 'instant', points }
}

/** The sensor's own chart on its page: every stored reading, so the time
 *  axis spans the sensor's own window, first reading to last. Alone on its
 *  chart it has one Y axis whatever its group, and the chart titles an axis
 *  by its group, so the group carries the reader's own words there:
 *  "Water level (m)", not the key "manual:water_level:m (m)". Beside other
 *  sources (manualSensorSeries) the key stays, keeping their axes apart. */
export function manualChartSeries(sensor: ManualSensorReadings): Series {
  return { ...manualSensorSeries(sensor), axis_group: sensor.quantity }
}

/** Each reading's note by its instant in epoch ms, as the chart's tooltip
 *  looks a reading up (TimeSeriesChart `pointNote`). */
export function manualReadingNotes(sensor: WithReadings): Map<number, string> {
  return new Map(sensor.readings.flatMap(reading => reading.note ? [[Date.parse(reading.ts), reading.note] as const] : []))
}

// ----------------------------------------------------------- sensors

const STATION_CODE = /^[^|,\s]{1,64}$/

/** The form's fields, with the sentence that says what to change. Names are
 *  unique among a project's manual sensors, case aside, as the server's
 *  index holds them (db/034). */
export function validateManualSensorInput(input: ManualSensorInput,
    others: Pick<ManualSensor, 'id' | 'name' | 'project_id'>[] = [], id?: string): string | null {
  const name = input.name.trim()
  if (!name || name.length > 100) return 'Enter a name of 100 characters or fewer.'
  if (input.project_id === null) return 'Choose a project.'
  if (others.some(other => other.id !== id && other.project_id === input.project_id
      && other.name.trim().toLocaleLowerCase() === name.toLocaleLowerCase())) {
    return 'A manual sensor with this name already exists in this project.'
  }
  if (!SENSOR_TYPES.includes(input.sensor_type as SensorType)) return 'Choose a sensor type.'
  if (!input.quantity.trim() || input.quantity.trim().length > 80) return 'Enter what is measured, in 80 characters or fewer.'
  if (input.unit.trim().length > 30) return 'Keep the unit to 30 characters.'
  if (!Number.isInteger(input.precision) || input.precision < 0 || input.precision > 9) return 'Choose 0 to 9 decimal places.'
  if (input.station !== null && !STATION_CODE.test(input.station)) return 'Choose a station from the list, or none.'
  if (input.notes.length > MAX_SENSOR_NOTES) return `Keep the notes to ${MAX_SENSOR_NOTES.toLocaleString('en-GB')} characters.`
  return null
}

export function inputFor(sensor: ManualSensor): ManualSensorInput {
  return { name: sensor.name, sensor_type: sensor.sensor_type, quantity: sensor.quantity, unit: sensor.unit,
    precision: sensor.precision, station: sensor.station, project_id: sensor.project_id, notes: sensor.notes }
}

function writeFor(input: ManualSensorInput): ManualSensorWrite {
  const invalid = validateManualSensorInput(input)
  if (invalid) throw new Error(invalid)
  return { project_id: input.project_id!, name: input.name.trim(), sensor_type: input.sensor_type as SensorType,
    quantity: input.quantity.trim(), unit: input.unit.trim(), precision: input.precision, station: input.station,
    notes: input.notes.trim() }
}

const STORED_TS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/

/** The stored form is the identity, so it must be the one canonical
 *  spelling of its instant: the same instant written with another offset
 *  would slip past the one-reading-per-time rule. */
function canonicalTime(ts: unknown): ts is string {
  return typeof ts === 'string' && STORED_TS.test(ts) && toStationIso(new Date(ts)) === ts
}

// ------------------------------------------------------------ server

/** Every manual sensor in the reader's projects, held once for every page
 *  (the list, the Data Explorer's sources, workspace search). A write here
 *  asks again, so each page sees it at once. */
export const manualSensorStore = remoteCollection<ManualSensor>(
  () => api.manualSensors.list().then(data => data.sensors), MANUAL_SENSORS_EVENT)

/** What is held now, for code that cannot wait (a source list being built). */
export function readManualSensors(): ManualSensor[] {
  return manualSensorStore.snapshot().items
}

/** Create a sensor, or change one's properties. A new unit or quantity
 *  relabels the readings; it never converts them. */
export async function saveManualSensor(input: ManualSensorInput, id?: string): Promise<ManualSensor> {
  const body = writeFor(input)
  const { sensor } = id ? await api.manualSensors.update(id, body) : await api.manualSensors.create(body)
  await manualSensorStore.refresh()
  return sensor
}

/** Hidden on the server, never removed; its readings are kept there. */
export async function deleteManualSensor(id: string): Promise<void> {
  await api.manualSensors.remove(id)
  await manualSensorStore.refresh()
}

/** Every stored reading of one sensor (or those in a window), in time order. */
export async function loadManualReadings(id: string, window: { from?: Date; to?: Date } = {}): Promise<ManualReading[]> {
  return sortReadings((await api.manualSensors.readings(id, window)).readings)
}

/** Send a plan's new and replacing rows. Replacing needs the reader's word,
 *  given once the preview showed them (the server refuses otherwise). */
export async function applyReadings(id: string, plan: ImportPlan, origin: 'typed' | 'import' = 'typed',
    importName: string | null = null): Promise<{ added: number; replaced: number; unchanged: number }> {
  if (plan.blocked) throw new Error(plan.blocked)
  const rows = plan.rows.filter(row => row.status !== 'unchanged')
    .map(row => ({ ts: row.ts, value: row.value, note: row.note ?? '' }))
  if (!rows.length) return { added: 0, replaced: 0, unchanged: plan.unchanged }
  const done = await api.manualSensors.addReadings(id, { rows, replace: plan.replace > 0, origin, import_name: importName })
  await manualSensorStore.refresh()
  return done
}

/** An edited row. Moved onto a time that holds another reading, it replaces
 *  that one only when `replace` says the reader agreed. */
export async function changeReading(reading: ManualReading, entry: ReadingEntry, replace = false): Promise<boolean> {
  if (!editChanges(reading, entry)) return false
  const note = cleanNote(entry.note)
  if (note.length > MAX_READING_NOTE) throw new Error(`Keep the note to ${MAX_READING_NOTE} characters.`)
  const { changed } = await api.manualSensors.updateReading(reading.id, { ts: entry.ts, value: entry.value, note, replace })
  await manualSensorStore.refresh()
  return changed
}

export async function removeReading(reading: ManualReading): Promise<void> {
  await api.manualSensors.removeReading(reading.id)
  await manualSensorStore.refresh()
}

// --------------------------------------------------------------- CSV

const DIALECT: Record<CsvFormat, { delimiter: string; decimal: string }> = {
  'csv-excel': { delimiter: ';', decimal: ',' },
  'csv': { delimiter: ',', decimal: '.' },
}

/** A cell as the platform's CSV writes one; a note Excel would run as a
 *  formula (=, +, -, @) gets a leading apostrophe, as the analysis export
 *  does, and cleanNote drops it on the way back in. */
function csvCell(text: string, delimiter: string): string {
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text
  return safe.includes(delimiter) || /["\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

function valueHeader(sensor: Pick<ManualSensor, 'name' | 'unit'>): string {
  return `${sensor.name}${sensor.unit ? ` (${sensor.unit})` : ''}`
}

/** The readings as the platform's CSV dialects write them (Explore
 *  sensors' and the station export's): a BOM so Excel keeps °C and m³,
 *  station wall clock with the zone in the header, each value at the
 *  sensor's precision unless that would lose a digit. Three columns, the
 *  same three the import reads, so an exported file edited in Excel can be
 *  pasted or imported straight back. */
export function exportManualReadings(sensor: Pick<ManualSensorReadings, 'name' | 'unit' | 'precision' | 'readings'>, format: CsvFormat): string {
  const { delimiter, decimal } = DIALECT[format]
  const lines = [[`Time (${STATION_TZ}, UTC+4)`, valueHeader(sensor), 'Note'].map(cell => csvCell(cell, delimiter)).join(delimiter)]
  for (const reading of sortReadings(sensor.readings)) {
    lines.push([csvTime(reading.ts), csvValue(reading.value, sensor.precision, decimal),
      csvCell(reading.note ?? '', delimiter)].join(delimiter))
  }
  return `\uFEFF${lines.join('\r\n')}\r\n`
}

/** An empty file to fill in, offline or in Excel: the header and one example
 *  row showing the format. The example starts with #, which the import
 *  skips, so a template filled in below it never brings the example in as
 *  a reading. */
export function manualReadingsTemplate(sensor: Pick<ManualSensor, 'name' | 'unit'>, format: CsvFormat): string {
  const { delimiter, decimal } = DIALECT[format]
  const header = [`Time (${STATION_TZ}, UTC+4)`, valueHeader(sensor), 'Note'].map(cell => csvCell(cell, delimiter)).join(delimiter)
  const example = ['# 2026-10-22 14:30', '1.25'.replace('.', decimal), csvCell('Example: lines starting with # are skipped', delimiter)].join(delimiter)
  return `\uFEFF${header}\r\n${example}\r\n`
}

/** A file name that says what and when: the sensor and the station-local
 *  dates of its first and last readings. */
export function manualExportFileName(sensor: Pick<ManualSensorReadings, 'name' | 'readings'>, suffix = 'readings'): string {
  const slug = sensor.name.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'manual-sensor'
  const dates = sensor.readings.length
    ? `_${toStationIso(new Date(sensor.readings[0]!.ts)).slice(0, 10)}_${toStationIso(new Date(sensor.readings[sensor.readings.length - 1]!.ts)).slice(0, 10)}`
    : ''
  return `${slug}_${suffix}${dates}.csv`
}
